import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiMapPin, FiSearch, FiNavigation, FiLink } from 'react-icons/fi';
import './MapPicker.css';

const MapPicker = ({
  coordinates,
  onLocationSelect,
  onAddressSelect,
  height = '400px'
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [mapsLink, setMapsLink] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);

  // Extract coordinates from Google Maps link
  const extractCoordsFromLink = (link) => {
    try {
      // Pattern 1: @lat,lng or ?q=lat,lng
      const pattern1 = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
      const pattern2 = /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/;
      const pattern3 = /place\/.*\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
      const pattern4 = /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/;

      let match = link.match(pattern1) || link.match(pattern2) || link.match(pattern3) || link.match(pattern4);

      if (match) {
        return {
          latitude: parseFloat(match[1]),
          longitude: parseFloat(match[2])
        };
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  // Handle paste maps link
  const handlePasteMapsLink = async () => {
    let linkToProcess = mapsLink.trim();

    // Check if it's a short link that needs expanding
    if (linkToProcess.includes('goo.gl') || linkToProcess.includes('maps.app.goo.gl')) {
      alert('Short links are not supported. Please open the link in your browser, then copy the full URL from the address bar.');
      return;
    }

    const coords = extractCoordsFromLink(linkToProcess);
    if (coords) {
      onLocationSelect(coords);

      // Update map if loaded
      if (markerRef.current && mapInstanceRef.current) {
        const pos = new window.google.maps.LatLng(coords.latitude, coords.longitude);
        markerRef.current.setPosition(pos);
        mapInstanceRef.current.setCenter(pos);
        mapInstanceRef.current.setZoom(17);
        reverseGeocode(coords.latitude, coords.longitude);
      }

      setMapsLink('');
      setShowLinkInput(false);
      alert('Location set successfully!');
    } else {
      alert('Could not extract location. Please paste the FULL Google Maps URL (not the short link). Open the link first, then copy from address bar.');
    }
  };

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          setMapLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    const defaultCenter = {
      lat: coordinates?.latitude || 12.9716,
      lng: coordinates?.longitude || 77.5946
    };

    try {
      const map = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true
      });

      mapInstanceRef.current = map;

      // Add marker
      const marker = new window.google.maps.Marker({
        position: defaultCenter,
        map: map,
        draggable: true,
        animation: window.google.maps.Animation.DROP
      });

      markerRef.current = marker;

      // Handle marker drag
      marker.addListener('dragend', () => {
        const position = marker.getPosition();
        const lat = position.lat();
        const lng = position.lng();

        onLocationSelect({ latitude: lat, longitude: lng });
        reverseGeocode(lat, lng);
      });

      // Handle map click
      map.addListener('click', (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        marker.setPosition(e.latLng);
        onLocationSelect({ latitude: lat, longitude: lng });
        reverseGeocode(lat, lng);
      });

    } catch (err) {
      setError('Failed to initialize map');
      console.error('Map init error:', err);
    }
  }, [mapLoaded]);

  // Update marker when coordinates change
  useEffect(() => {
    if (markerRef.current && coordinates?.latitude && coordinates?.longitude) {
      const newPos = new window.google.maps.LatLng(
        parseFloat(coordinates.latitude),
        parseFloat(coordinates.longitude)
      );
      markerRef.current.setPosition(newPos);
      mapInstanceRef.current?.setCenter(newPos);
    }
  }, [coordinates?.latitude, coordinates?.longitude]);

  // Reverse geocode to get address
  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!window.google) return;

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results && response.results[0]) {
        const result = response.results[0];
        const addressComponents = result.address_components;

        // Parse address components
        const address = {
          full: result.formatted_address,
          building: '',
          landmark: '',
          pincode: ''
        };

        addressComponents.forEach(component => {
          if (component.types.includes('premise') || component.types.includes('subpremise')) {
            address.building = component.long_name;
          }
          if (component.types.includes('sublocality_level_1') || component.types.includes('sublocality')) {
            address.landmark = component.long_name;
          }
          if (component.types.includes('postal_code')) {
            address.pincode = component.long_name;
          }
        });

        onAddressSelect && onAddressSelect(address);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    }
  }, [onAddressSelect]);

  // Search for location
  const handleSearch = async () => {
    if (!searchQuery.trim() || !window.google) return;

    setSearching(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ address: searchQuery });

      if (response.results && response.results[0]) {
        const location = response.results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        mapInstanceRef.current?.setCenter(location);
        mapInstanceRef.current?.setZoom(17);
        markerRef.current?.setPosition(location);

        onLocationSelect({ latitude: lat, longitude: lng });
        reverseGeocode(lat, lng);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        const pos = new window.google.maps.LatLng(lat, lng);
        mapInstanceRef.current?.setCenter(pos);
        mapInstanceRef.current?.setZoom(17);
        markerRef.current?.setPosition(pos);

        onLocationSelect({ latitude: lat, longitude: lng });
        reverseGeocode(lat, lng);
      },
      (err) => {
        console.error('Geolocation error:', err);
        alert('Unable to get your location. Please enable location access.');
      },
      { enableHighAccuracy: true }
    );
  };

  // If no API key, show a simple coordinate input with link paste option
  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="map-picker-fallback">
        <div className="fallback-header">
          <FiMapPin />
          <span>Set Your Location</span>
        </div>
        <p className="fallback-note">
          Choose one of the options below to set your delivery location:
        </p>

        <div className="fallback-options">
          {/* Option 1: Current Location */}
          <button
            type="button"
            className="btn btn-primary get-location-btn"
            onClick={getCurrentLocation}
          >
            <FiNavigation />
            Use My Current Location
          </button>

          {/* Option 2: Paste Google Maps Link */}
          <div className="link-paste-section">
            <button
              type="button"
              className="btn btn-outline link-btn"
              onClick={() => setShowLinkInput(!showLinkInput)}
            >
              <FiLink />
              Paste Google Maps Link
            </button>

            {showLinkInput && (
              <div className="link-input-wrapper">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Paste Google Maps link here..."
                  value={mapsLink}
                  onChange={(e) => setMapsLink(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePasteMapsLink}
                  disabled={!mapsLink.trim()}
                >
                  Set Location
                </button>
              </div>
            )}
            <p className="link-help">
              Open Google Maps → Find your location → Copy the FULL URL from browser address bar (not the short share link)
            </p>
          </div>
        </div>

        {coordinates?.latitude && coordinates?.longitude && (
          <div className="coordinates-display success">
            <FiMapPin />
            <span>Location set: {parseFloat(coordinates.latitude).toFixed(6)}, {parseFloat(coordinates.longitude).toFixed(6)}</span>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-picker-error">
        <FiMapPin />
        <p>{error}</p>
        <button
          type="button"
          className="btn btn-outline"
          onClick={getCurrentLocation}
        >
          <FiNavigation />
          Use My Current Location
        </button>
      </div>
    );
  }

  return (
    <div className="map-picker">
      <div className="map-search">
        <div className="search-input-wrapper">
          <FiSearch />
          <input
            type="text"
            placeholder="Search for a location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary search-btn"
          onClick={handleSearch}
          disabled={searching}
        >
          {searching ? 'Searching...' : 'Search'}
        </button>
        <button
          type="button"
          className="btn btn-outline location-btn"
          onClick={getCurrentLocation}
          title="Use my current location"
        >
          <FiNavigation />
        </button>
      </div>

      <div
        ref={mapRef}
        className="map-container"
        style={{ height }}
      >
        {!mapLoaded && (
          <div className="map-loading">
            <div className="spinner"></div>
            <p>Loading map...</p>
          </div>
        )}
      </div>

      <p className="map-hint">
        <FiMapPin /> Click on the map or drag the marker to set your exact location
      </p>

      {/* Link paste option */}
      <div className="map-link-option">
        <button
          type="button"
          className="btn btn-outline btn-sm link-toggle"
          onClick={() => setShowLinkInput(!showLinkInput)}
        >
          <FiLink />
          {showLinkInput ? 'Hide' : 'Paste Google Maps Link'}
        </button>

        {showLinkInput && (
          <div className="link-input-row">
            <input
              type="text"
              className="form-input"
              placeholder="Paste Google Maps link..."
              value={mapsLink}
              onChange={(e) => setMapsLink(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handlePasteMapsLink}
              disabled={!mapsLink.trim()}
            >
              Set
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapPicker;
